import { useState } from "react";
import { api, fmtBRL } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Eye, Undo2, Loader2 } from "lucide-react";
import { AcertoTable } from "./AcertoTable";
import { EditarAcertoForm } from "./EditarAcertoForm";
import { fmtD, fmtDT } from "./acertoHelpers";

const Dif = ({ v }) => v == null ? <span className="text-slate-400">—</span>
  : <span className={`mono font-semibold ${v === 0 ? "text-emerald-700" : v < 0 ? "text-rose-700" : "text-sky-700"}`}>{v === 0 ? "OK" : (v > 0 ? "+" : "") + fmtBRL(v)}</span>;

/** Histórico de acertos registrados (detalhe, edição com acertos.A e "desfazer" com acertos.E). */
export function AcertosHistorico({ acertos, loading, canEdit, canUndo, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [undo, setUndo] = useState(null);
  const [busy, setBusy] = useState(null);
  const ver = async (a) => {
    try { const r = await api.get(`/acertos/${a.id}`); setDetail(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao abrir acerto"); }
  };
  const desfazer = async (a) => {
    setUndo(null);
    setBusy(a.id);
    try { await api.delete(`/acertos/${a.id}`); toast.success("Acerto desfeito"); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao desfazer"); }
    finally { setBusy(null); }
  };
  return (
    <div className="rounded-xl border bg-white overflow-hidden" data-testid="acertos-historico">
      <Table wrapperClassName="max-h-[40vh] overflow-auto">
        <TableHeader className="sticky top-0 bg-white z-10">
          <TableRow className="hover:bg-white">
            <TableHead>Registrado</TableHead><TableHead>Entregador</TableHead><TableHead>Período</TableHead><TableHead className="text-right">Entregas</TableHead>
            <TableHead className="text-right">Troco saiu</TableHead><TableHead className="text-right">A receber</TableHead><TableHead className="text-right">Conferido</TableHead><TableHead>Dif.</TableHead><TableHead>Por</TableHead><TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {acertos.map((a) => (
            <TableRow key={a.id} data-testid={`acerto-hist-${a.id}`}>
              <TableCell className="text-xs mono">{fmtDT(a.created_at)}</TableCell>
              <TableCell className="text-xs font-medium">{a.entregador_nome}</TableCell>
              <TableCell className="text-xs mono">{fmtD(a.start)}{a.start !== a.end ? ` → ${fmtD(a.end)}` : ""}</TableCell>
              <TableCell className="text-xs text-right">{a.qtd}</TableCell>
              <TableCell className="text-xs text-right mono text-orange-700">{fmtBRL(a.totais?.troco_saiu)}</TableCell>
              <TableCell className="text-xs text-right mono font-semibold">{fmtBRL(a.totais?.a_receber)}</TableCell>
              <TableCell className="text-xs text-right mono">{a.valor_conferido != null ? fmtBRL(a.valor_conferido) : "—"}</TableCell>
              <TableCell className="text-xs"><Dif v={a.diferenca} /></TableCell>
              <TableCell className="text-xs text-slate-500">{a.created_by_nome || "—"}</TableCell>
              <TableCell>
                <div className="flex gap-1 justify-end">
                  <Button size="icon" variant="ghost" onClick={() => ver(a)} title="Ver entregas" data-testid={`acerto-ver-${a.id}`}><Eye className="w-4 h-4 text-slate-600" /></Button>
                  {canUndo && <Button size="icon" variant="ghost" onClick={() => setUndo(a)} disabled={busy === a.id} title="Desfazer acerto" data-testid={`acerto-desfazer-${a.id}`}>
                    {busy === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4 text-rose-600" />}</Button>}
                </div>
              </TableCell>
            </TableRow>
          ))}
          {acertos.length === 0 && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-6" data-testid="acertos-historico-vazio">{loading ? "Carregando…" : "Nenhum acerto registrado ainda."}</TableCell></TableRow>}
        </TableBody>
      </Table>
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-4xl" data-testid="acerto-detalhe">
          <DialogHeader><DialogTitle>Acerto · {detail?.entregador_nome} · {fmtD(detail?.start)}{detail && detail.start !== detail.end ? ` → ${fmtD(detail.end)}` : ""}</DialogTitle>
            <DialogDescription>Registrado em {fmtDT(detail?.created_at)} por {detail?.created_by_nome || "—"} · {detail?.totais?.qtd ?? 0} entrega(s)</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-3 text-sm">
                <span>Troco que saiu: <b className="mono text-orange-700">{fmtBRL(detail.totais?.troco_saiu)}</b></span>
                <span>A receber: <b className="mono">{fmtBRL(detail.totais?.a_receber)}</b></span>
                <span>Conferido: <b className="mono">{detail.valor_conferido != null ? fmtBRL(detail.valor_conferido) : "—"}</b></span>
                <span>Diferença: <Dif v={detail.diferenca} /></span>
                {detail.observacao && <span className="text-slate-600" data-testid="acerto-detalhe-obs">Obs.: {detail.observacao}</span>}
              </div>
              {canEdit && <EditarAcertoForm key={detail.id + (detail.updated_at || "")} acerto={detail}
                onSaved={(d) => { setDetail((cur) => ({ ...cur, ...d, entregas: cur.entregas })); onChanged?.(); }} />}
              <AcertoTable entregas={detail.entregas || []} selected={new Set()} readOnly />
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!undo} onOpenChange={(o) => !o && setUndo(null)}>
        <AlertDialogContent data-testid="acerto-desfazer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Desfazer acerto?</AlertDialogTitle>
            <AlertDialogDescription>
              {undo && `Acerto de ${undo.entregador_nome} (${fmtD(undo.start)}${undo.start !== undo.end ? ` a ${fmtD(undo.end)}` : ""}). As ${undo.qtd} entrega(s) voltam a ficar pendentes de acerto.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="acerto-desfazer-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => desfazer(undo)} data-testid="acerto-desfazer-confirmar">Desfazer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
